import {Helmet} from '@dr.pogodin/react-helmet';
import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {DataStatus, Parameter} from 'platform/components';
import {Show} from 'platform/foundation';
import {formatPhoneNumber, useDateTimeFormatter} from 'platform/locale';

import {isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty, isPositive} from 'ramda-adjunct';

import {useGetCommentsQuery, useGetNoteQuery, useGetTireSetCountQuery} from '@dms/api/core';
import {useGetCustomerV2Query, useGetVehicleCustomerQuery} from '@dms/api/customer';
import {useGetVehicleQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {
  useGetPostponeJobsByVehicleIdQuery,
  useGetServiceCaseByVehicleCountQuery,
  useGetVehicleServiceCampaignQuery,
  useGetWorkshopInterestQuery,
} from '@dms/api/workshop';
import {featureFlags} from '@dms/feature-flags';
import {CustomNoticeList, useCustomNoticesBadge} from '@dms/features/custom-notices';
import {TaskGridPage, useTasksCount} from '@dms/features/tasks';
import {TelephonyCallRecordVisit} from '@dms/features/telephony-call';
import i18n from '@dms/i18n';
import {customerRoutes, testIds, vehiclesRoutes, workshopRoutes} from '@dms/routes';
import {
  Checklists,
  CommunicationSection,
  EMPTY_PLACEHOLDER,
  EntityHeaderFlag,
  EntityHeader,
  getCommunicationSectionTitle,
  getCustomerEmail,
  getCustomerName,
  getCustomerPhoneNumber,
  getEmailParameter,
  getNaturalPersonFullName,
  getTimeToFirstResponse,
  isCustomerNaturalPerson,
  Main,
  NavigationItem,
  Page,
  Section,
  ServiceCampaigns,
  SnippetProps,
  useChecklist,
  usePermissions,
  useWorkshopInterestEditPermissions,
  VehicleActiveServiceCases,
  VehicleHistoryOfRepairs,
  VehiclePostponedJobs,
} from '@dms/shared';
import {TiresOverview} from '@dms/shared-aftersales';

import {
  buildArray,
  composePath,
  parseDate,
  suffixTestId,
  useNavigate,
  useRequiredParams,
} from 'shared/web';

import {ActionLog} from './(sections)/ActionLog/ActionLog';
import {Customer} from './(sections)/Customer/Customer';
import {Overview} from './(sections)/Overview/Overview';
import {Ownership} from './(sections)/Ownership/Ownership';
import {ServiceRequest} from './(sections)/ServiceRequest/ServiceRequest';
import {WorkshopInterestActivityPanel} from './components/WorkshopInterestActivityPanel';
import {useWorkshopInterestActions} from './hooks/useWorkshopInterestActions';
import {useWorkshopInterestDropdownFlags} from './hooks/useWorkshopInterestDropdownFlags';

// eslint-disable-next-line complexity, max-lines-per-function, max-statements -- Pre-existing violation
export function WorkshopInterestDetail() {
  const {id: interestId} = useRequiredParams();
  const formatDateTime = useDateTimeFormatter();
  const navigate = useNavigate();

  const {
    data: workshopInterest,
    isLoading,
    isError,
  } = useGetWorkshopInterestQuery({id: interestId});

  const isCurrentInterest = workshopInterest?.id === interestId;

  const {currentData: customer} = useGetCustomerV2Query(
    isCurrentInterest && isNotNil(workshopInterest?.customerId)
      ? {customerId: workshopInterest.customerId}
      : skipToken,
    {refetchOnMountOrArgChange: true}
  );
  const vehicleId = isCurrentInterest ? workshopInterest?.vehicleId : undefined;

  const {currentData: vehicle} = useGetVehicleQuery(isNotNil(vehicleId) ? {vehicleId} : skipToken, {
    refetchOnMountOrArgChange: true,
  });

  const {vehicleOperator, vehicleCustomersCount} = useGetVehicleCustomerQuery(
    isNotNil(vehicleId) ? {vehicleId} : skipToken,
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: (result) => ({
        vehicleOperator: result.currentData?.find(
          (item) => item?.customerType === 'CORVEHICLECUSTOMERTYPE_OPERATOR'
        ),
        vehicleCustomersCount: result.currentData?.length ?? 0,
      }),
    }
  );

  const {currentData: postponedJobsData} = useGetPostponeJobsByVehicleIdQuery(
    isNotNil(vehicleId) ? {vehicleId} : skipToken
  );
  const {currentData: activeServiceCasesCount} = useGetServiceCaseByVehicleCountQuery(
    isNotNil(vehicleId) ? {vehicleId, status: 'ACTIVE'} : skipToken
  );
  const {currentData: finishedServiceCasesCount} = useGetServiceCaseByVehicleCountQuery(
    isNotNil(vehicleId) ? {vehicleId, status: 'FINISHED'} : skipToken
  );
  const {currentData: vehicleServiceCampaign} = useGetVehicleServiceCampaignQuery(
    isNotNil(vehicleId) ? {vehicleId} : skipToken
  );
  const {currentData: vehicleTires} = useGetTireSetCountQuery(
    isNotNil(vehicleId) ? {vehicleId, states: ['INSTOCK']} : skipToken
  );

  const activeServiceCampaignsCount =
    vehicleServiceCampaign?.serviceCampaign?.filter(
      (campaign) => campaign.kind === 'ACTIVE_CAMPAIGN'
    ).length ?? 0;

  const {currentData: operatorCustomerData} = useGetCustomerV2Query(
    isNotNil(vehicleOperator?.customerId) ? {customerId: vehicleOperator.customerId} : skipToken,
    {refetchOnMountOrArgChange: true}
  );

  const operatorCustomer = isNotNil(vehicleId) ? operatorCustomerData : undefined;

  const {data: note} = useGetNoteQuery({
    resourceId: EntityResourceIds.workshopInterest,
    recordId: interestId,
    context: EntityResourceIds.workshopInterest,
  });

  const {data: comments = []} = useGetCommentsQuery({
    resourceId: EntityResourceIds.workshopInterest,
    recordId: interestId,
  });

  const {tag, participation} = useWorkshopInterestEditPermissions({interestId});
  const [canReadPneuhotel] = usePermissions({permissionKeys: ['pneuhotelRead']});

  const actions = useWorkshopInterestActions(interestId);
  const dropdownFlags = useWorkshopInterestDropdownFlags(interestId);

  const {badgeSettings, checklistSettings} = useChecklist({
    recordId: interestId,
    resourceId: 'WORKSHOP_INTEREST',
  });

  const {count: tasksTodoCount} = useTasksCount({
    entityId: interestId,
    relation: EntityResourceIds.workshopInterest,
    state: 'TODO',
  });
  const {count: tasksInProgressCount} = useTasksCount({
    entityId: interestId,
    relation: EntityResourceIds.workshopInterest,
    state: 'IN_PROGRESS',
  });
  const tasksCountBadge = tasksTodoCount + tasksInProgressCount;

  const {customNoticesBadge} = useCustomNoticesBadge({
    resourceId: EntityResourceIds.workshopInterest,
    recordId: interestId,
  });

  const {timeToFirstResponse, hasEnded} = getTimeToFirstResponse(
    workshopInterest,
    workshopInterest?.firstContactAt
  );

  const contactPerson = isNotNil(workshopInterest?.contactPersonId)
    ? customer?.contacts?.find((person) => person.id === workshopInterest?.contactPersonId)
    : undefined;

  const personName = isNotNil(contactPerson)
    ? (getNaturalPersonFullName(contactPerson) ?? EMPTY_PLACEHOLDER)
    : (getCustomerName(customer) ?? EMPTY_PLACEHOLDER);

  const title = isNotNil(vehicle) ? `${vehicle.title} - ${personName}` : personName;

  const flags = buildArray<EntityHeaderFlag>()
    .when(workshopInterest?.state === 'NEW', {
      colorScheme: 'blue',
      label: i18n.t('general.labels.new'),
      isSubtle: true,
    })
    .when(workshopInterest?.state === 'OPEN', {
      colorScheme: 'green',
      label: i18n.t('general.actions.open'),
      isSubtle: true,
    })
    .when(workshopInterest?.state === 'CLOSED', {
      colorScheme: 'green',
      label: i18n.t('general.labels.closed'),
      isSubtle: true,
    })
    .when(workshopInterest?.state === 'UNSUCCESSFUL', {
      colorScheme: 'red',
      label: i18n.t('general.labels.unsuccessful'),
      isSubtle: true,
    })
    .add({
      colorScheme: 'orange',
      label: i18n.t('page.workshop.labels.interest'),
      isSubtle: false,
    });

  const onNoteCommentIconClick = () => {
    navigate(composePath(workshopRoutes.interestDetailOverview, {params: {id: interestId}}));
  };

  const parameters = buildArray<Parameter>()
    .when(isNotNilOrEmpty(note?.text), {
      icon: 'action/description',
      title: null,
      variant: 'secondary',
      tooltip: note?.text,
      onClick: onNoteCommentIconClick,
    })
    .when(comments.length > 0, {
      icon: 'communication/comment',
      title: null,
      variant: 'secondary',
      hideSeparator: true,
      onClick: onNoteCommentIconClick,
    })
    .when(isNotNil(workshopInterest?.publicId), workshopInterest?.publicId)
    .when(
      isNotNil(workshopInterest?.createdAt),
      `${i18n.t('general.labels.created')}: ${
        isNotNil(workshopInterest?.createdAt)
          ? formatDateTime('dateTimeShort', parseDate(workshopInterest.createdAt))
          : EMPTY_PLACEHOLDER
      }`
    )
    .when(isNotNilOrEmpty(timeToFirstResponse), {
      title: timeToFirstResponse,
      icon: 'devices/access_time',
      variant: hasEnded ? 'success' : 'danger',
      tooltip: i18n.t('general.label.timeToFirstResponse'),
    });

  const contactPersonPhoneNumber = contactPerson?.phoneNumbers?.[0];
  const contactPersonEmail = contactPerson?.emails?.[0]?.email;
  const formattedContactPersonPhoneNumber = isNotNil(contactPersonPhoneNumber)
    ? formatPhoneNumber(
        `${contactPersonPhoneNumber.prefix}${contactPersonPhoneNumber.number}`,
        contactPersonPhoneNumber.countryCode
      )
    : null;

  const contactSnippetTitle = isNotNil(contactPerson)
    ? getNaturalPersonFullName(contactPerson)
    : getCustomerName(customer);

  const snippets = buildArray<SnippetProps>()
    .add({
      'data-testid': testIds.workshop.interestDetail('contactPerson-snippet'),
      resourceId: EntityResourceIds.workshopInterest,
      recordId: interestId,
      label: i18n.t('entity.businessCase.labels.contactPerson'),
      placeholder: {
        title: i18n.t('entity.businessInfo.labels.noContactPerson'),
      },
      title: contactSnippetTitle ?? undefined,
      parameters: buildArray<Parameter>()
        .when(
          isNotNil(contactPerson) && isNotNil(formattedContactPersonPhoneNumber),
          formattedContactPersonPhoneNumber
        )
        .when(
          isNotNil(contactPerson) && isNotNilOrEmpty(contactPersonEmail),
          getEmailParameter(contactPersonEmail)
        )
        .when(isNil(contactPerson) && isNotNil(customer), getCustomerPhoneNumber(customer))
        .when(
          isNil(contactPerson) && isNotNil(customer),
          getEmailParameter(getCustomerEmail(customer))
        ),
      href: customer?.id
        ? composePath(customerRoutes.detail, {
            params: {id: customer.id},
            isSearchParamsPreserved: false,
          })
        : undefined,
      customerId: customer?.id,
      selectedContactId: contactPerson?.id,
    })
    .add({
      'data-testid': testIds.workshop.interestDetail('operator-snippet'),
      label: i18n.t('entity.ownership.label.vehicleOperator'),
      placeholder: {
        title: i18n.t('entity.ownership.actions.assignVehicleOperator'),
      },
      title: getCustomerName(operatorCustomer) ?? EMPTY_PLACEHOLDER,
      parameters: buildArray<Parameter>()
        .when(
          isNotNil(operatorCustomer) && !isCustomerNaturalPerson(operatorCustomer),
          getCustomerPhoneNumber(operatorCustomer)
        )
        .when(
          isNotNil(operatorCustomer) && !isCustomerNaturalPerson(operatorCustomer),
          getEmailParameter(getCustomerEmail(operatorCustomer))
        )
        .when(
          isNotNil(operatorCustomer) && isCustomerNaturalPerson(operatorCustomer),
          getCustomerPhoneNumber(operatorCustomer)
        )
        .when(
          isNotNil(operatorCustomer) && isCustomerNaturalPerson(operatorCustomer),
          getEmailParameter(getCustomerEmail(operatorCustomer))
        ),
      href: operatorCustomer?.id
        ? composePath(customerRoutes.detail, {
            params: {id: operatorCustomer.id},
            isSearchParamsPreserved: false,
          })
        : undefined,
    })
    .when(isNotNil(vehicleId), {
      'data-testid': testIds.workshop.interestDetail('vehicle-snippet'),
      label: i18n.t('entity.vehicle.labels.vehicle'),
      title: vehicle?.title ?? EMPTY_PLACEHOLDER,
      parameters: [
        vehicle?.vin ?? i18n.t('entity.vehicle.labels.noVin'),
        vehicle?.vehicle?.registrationPlate ??
          i18n.t('entity.vehicle.notifications.noLicencePlate'),
      ],
      href: isNotNil(vehicleId)
        ? composePath(vehiclesRoutes.detail, {
            params: {id: vehicleId},
            isSearchParamsPreserved: false,
          })
        : undefined,
      vehicleId,
    });

  const maximization = {
    enabled: true,
    title: `${i18n.t('entity.customerMessage.labels.contextResource.WORKSHOP_INTEREST')}: ${workshopInterest?.publicId ?? EMPTY_PLACEHOLDER}`,
  };

  const navigation = buildArray<NavigationItem>()
    .add({
      id: 'overview',
      label: i18n.t('general.labels.overview'),
      href: composePath(workshopRoutes.interestDetailOverview, {params: {id: interestId}}),
      'data-testid': testIds.workshop.interestDetail('section-overview'),
      content: <Overview interestId={interestId} />,
    })
    .add({
      id: 'customer',
      label: i18n.t('entity.customer.labels.customer'),
      href: composePath(workshopRoutes.interestDetailCustomer, {params: {id: interestId}}),
      'data-testid': testIds.workshop.interestDetail('section-customer'),
      content: <Customer interestId={interestId} />,
    })
    .add({
      id: 'serviceRequest',
      label: i18n.t('entity.order.labels.serviceRequest'),
      href: composePath(workshopRoutes.interestDetailServiceRequest, {params: {id: interestId}}),
      'data-testid': testIds.workshop.interestDetail('section-serviceRequest'),
      content: <ServiceRequest interestId={interestId} />,
      hasSeparator: true,
    })
    .add({
      id: 'checklist',
      label: i18n.t('entity.checklist.labels.title'),
      href: composePath(workshopRoutes.interestDetailChecklist, {params: {id: interestId}}),
      'data-testid': testIds.workshop.interestDetail('section-checklist'),
      content: (
        <Section>
          <Checklists {...checklistSettings} />
        </Section>
      ),
      badge: badgeSettings,
    })
    .add({
      id: 'tasks',
      label: i18n.t('entity.task.labels.tasks'),
      href: composePath(workshopRoutes.interestDetailTasks, {params: {id: interestId}}),
      'data-testid': testIds.workshop.interestDetail('section-tasks'),
      content: (
        <TaskGridPage
          maximization={maximization}
          gridCode="workshop-interest-task"
          entityId={interestId}
          data-testid={testIds.workshop.interestDetail('tasks')}
        />
      ),
      badge: {
        colorScheme: 'neutral',
        value: tasksCountBadge,
      },
      hasSeparator: !isFeatureEnabled(featureFlags.CUSTOM_NOTICES),
    })
    .whenFeatureEnabled(featureFlags.CUSTOM_NOTICES, {
      id: 'customNotices',
      label: i18n.t('general.customNotices.labels.title'),
      href: composePath(workshopRoutes.interestDetailCustomNotices, {params: {id: interestId}}),
      'data-testid': testIds.workshop.interestDetail('section-customNotices'),
      content: (
        <CustomNoticeList
          resourceId={EntityResourceIds.workshopInterest}
          recordId={interestId}
          data-testid={testIds.workshop.interestDetail('customNotices')}
        />
      ),
      badge: customNoticesBadge,
      hasSeparator: true,
    })
    .add({
      id: 'postponedRequests',
      label: i18n.t('entity.vehicle.labels.postponedJobs'),
      href: composePath(workshopRoutes.interestDetailPostponedRequests, {
        params: {id: interestId},
      }),
      'data-testid': testIds.workshop.interestDetail('section-postponedRequests'),
      content: (
        <Show when={isNotNil(vehicleId)}>
          <VehiclePostponedJobs vehicleId={vehicleId ?? ''} />
        </Show>
      ),
      badge: {
        colorScheme: 'neutral',
        value: postponedJobsData?.count ?? 0,
      },
      isDisabled: isNil(vehicleId),
    })
    .add({
      id: 'ownership',
      label: i18n.t('entity.serviceCase.labels.ownership'),
      href: composePath(workshopRoutes.interestDetailOwnership, {params: {id: interestId}}),
      'data-testid': testIds.workshop.interestDetail('section-ownership'),
      content: <Ownership interestId={interestId} />,
      badge: {
        colorScheme: 'neutral',
        value: vehicleCustomersCount,
      },
      isDisabled: isNil(vehicleId),
    })
    .add({
      id: 'activeServiceCases',
      label: i18n.t('entity.vehicles.labels.activeServiceCases'),
      href: composePath(workshopRoutes.interestDetailActiveServiceCases, {
        params: {id: interestId},
      }),
      'data-testid': testIds.workshop.interestDetail('section-activeServiceCases'),
      content: (
        <Show when={isNotNil(vehicleId)}>
          <VehicleActiveServiceCases maximization={maximization} vehicleId={vehicleId ?? ''} />
        </Show>
      ),
      badge: {
        colorScheme: 'neutral',
        value: activeServiceCasesCount?.count ?? 0,
      },
      isDisabled: isNil(vehicleId),
    })
    .add({
      id: 'historyOfRepairs',
      label: i18n.t('entity.serviceCase.labels.historyOfRepairs'),
      href: composePath(workshopRoutes.interestDetailHistoryOfRepairs, {
        params: {id: interestId},
      }),
      'data-testid': testIds.workshop.interestDetail('section-historyOfRepairs'),
      content: (
        <Show when={isNotNil(vehicleId)}>
          <VehicleHistoryOfRepairs maximization={maximization} vehicleId={vehicleId} />
        </Show>
      ),
      badge: {
        colorScheme: 'neutral',
        value: finishedServiceCasesCount?.count ?? 0,
      },
      isDisabled: isNil(vehicleId),
    })
    .add({
      id: 'serviceCampaigns',
      label: i18n.t('entity.campaigns.labels.serviceCampaigns'),
      href: composePath(workshopRoutes.interestDetailServiceCampaigns, {
        params: {id: interestId},
      }),
      'data-testid': testIds.workshop.interestDetail('section-serviceCampaigns'),
      content: (
        <Show when={isNotNil(vehicleId)}>
          <Main data-testid={testIds.vehicles.serviceCampaigns('page')}>
            <ServiceCampaigns
              vehicleId={vehicleId ?? ''}
              data-testid={testIds.vehicles.serviceCampaigns('serviceCampaigns')}
            />
          </Main>
        </Show>
      ),
      badge: {
        colorScheme: 'neutral',
        value: activeServiceCampaignsCount,
      },
      isDisabled: isNil(vehicleId),
      hasSeparator: !canReadPneuhotel,
    })
    .when(canReadPneuhotel, {
      id: 'tires',
      label: i18n.t('page.tiresInventory.labels.tires'),
      href: composePath(workshopRoutes.interestDetailTires, {params: {id: interestId}}),
      'data-testid': testIds.workshop.interestDetail('section-tires'),
      content: (
        <Show when={isNotNil(vehicleId)}>
          <TiresOverview
            maximization={maximization}
            vehicleId={vehicleId ?? ''}
            customerId={workshopInterest?.customerId ?? ''}
          />
        </Show>
      ),
      badge: isPositive(vehicleTires?.count)
        ? {value: vehicleTires.count, colorScheme: 'neutral'}
        : undefined,
      isDisabled: isNil(vehicleId),
      hasSeparator: true,
    })
    .add({
      id: 'actionLog',
      label: i18n.t('general.labels.activityLogs'),
      href: composePath(workshopRoutes.interestDetailActionLog, {params: {id: interestId}}),
      'data-testid': testIds.workshop.interestDetail('section-actionLog'),
      content: <ActionLog maximization={maximization} />,
    })
    .add({
      id: 'communication',
      label: getCommunicationSectionTitle(),
      href: composePath(workshopRoutes.interestDetailCommunication, {params: {id: interestId}}),
      'data-testid': testIds.workshop.interestDetail('section-communication'),
      content: (
        <CommunicationSection
          maximization={maximization}
          customerId={workshopInterest?.customerId ?? ''}
          recordId={interestId}
          resourceId={EntityResourceIds.workshopInterest}
          data-testid="workshop-interest-communication"
        />
      ),
    });

  const callVisitDetails = [
    workshopInterest?.publicId,
    ...flags.filter((flag) => flag.isSubtle).map((flag) => flag.label),
  ];

  const detailTestId = suffixTestId('detail', {
    'data-testid': testIds.workshop.interestDetail('page'),
  });

  return (
    <>
      <Helmet title={i18n.t('page.workshop.labels.workshopInterest')} />
      <TelephonyCallRecordVisit
        resourceId={EntityResourceIds.workshopInterest}
        recordId={interestId}
        title={vehicle?.title ?? i18n.t('page.workshop.labels.workshopInterest')}
        details={callVisitDetails}
        customerName={getCustomerName(customer) ?? null}
      />
      <DataStatus key={workshopInterest?.id} isLoading={isLoading} isError={isError} minHeight={61}>
        <Page
          isFullHeight
          navigation={navigation}
          activityPanel={<WorkshopInterestActivityPanel interestId={interestId} />}
          data-testid={detailTestId}
          header={
            <EntityHeader
              title={title}
              icon="content/next_week"
              flags={flags}
              parameters={parameters}
              snippets={snippets}
              recordId={interestId}
              dropdownFlags={dropdownFlags}
              resourceId={EntityResourceIds.workshopInterest}
              controls={['WORKSHOP_INTEREST_BRANCH', 'WORKSHOP_INTEREST_SOURCE', 'ASSIGNEE']}
              actions={actions}
              permissions={{
                tag,
                participation,
              }}
              data-testid={detailTestId}
            />
          }
        />
      </DataStatus>
    </>
  );
}
